
import React, { lazy, Suspense } from 'react';
import { Route, Routes, Navigate } from 'react-router-dom';
import Dashboard from '../pages/Dashboard';
import Students from '../pages/StudentsList';
import StudentForm from '../pages/StudentForm';
import StudentDetails from '../pages/StudentDetails';
import TeachersList from '../pages/TeachersList';
import TeacherForm from '../pages/TeacherForm';
import TeacherDetails from '../pages/TeacherDetails';
import TeacherTimetable from '../pages/TeacherTimetable';
import TimetableAlerts from '../pages/TimetableAlerts';
import TimetableForm from '../pages/TimetableForm';
import TeacherAttendance from '../pages/TeacherAttendance';
import Reports from '../pages/Reports';
import Settings from '../pages/Settings';
import Search from '../pages/Search';
import NotFound from '../pages/NotFound';
import AboutUs from '../pages/AboutUs';
import TermsConditions from '../pages/TermsConditions';
import PrivacyPolicy from '../pages/PrivacyPolicy';
import HelpCenter from '../pages/HelpCenter';
import Documentation from '../pages/Documentation';
import TeachersOverviewPage from '../pages/TeachersOverviewPage';
import AdminsList from '../pages/AdminsList';
import AdminDetails from '../pages/AdminDetails';
import AdminForm from '../pages/AdminForm';
import CertificateTemplatesPage from '../pages/CertificateTemplatesPage';
import DocumentManagementPage from '../pages/DocumentManagementPage';

import ExamResultForm from '../pages/ExamResultForm';
import ProgramsListPage from '../pages/ProgramsListPage';
import NewStudentsPage from '../pages/NewStudentsPage';
import RevenuePage from '../pages/RevenuePage';
import TotalStudentsPage from '../pages/TotalStudentsPage';
import OfflinePage from '../components/common/OfflinePage';

// Import the BackupManagementPage
const BackupManagementPage = lazy(() => import('../pages/BackupManagementPage'));

// Payment processing pages
import TeacherPaymentPage from '../pages/payments/TeacherPaymentPage';
import StudentPaymentPage from '../pages/payments/StudentPaymentPage';

// Import the certificate template pages
import CreateTemplateBasicInfoPage from '../pages/certificate-templates/CreateTemplateBasicInfoPage';
import CreateTemplateUploadPage from '../pages/certificate-templates/CreateTemplateUploadPage';
import CreateTemplateMappingPage from '../pages/certificate-templates/CreateTemplateMappingPage';
import CreateTemplatePreviewPage from '../pages/certificate-templates/CreateTemplatePreviewPage';
import EditTemplateBasicInfoPage from '../pages/certificate-templates/EditTemplateBasicInfoPage';
import EditTemplateUploadPage from '../pages/certificate-templates/EditTemplateUploadPage';
import EditTemplateMappingPage from '../pages/certificate-templates/EditTemplateMappingPage';
import EditTemplatePreviewPage from '../pages/certificate-templates/EditTemplatePreviewPage';

import HistoryManagementPage from '../pages/admin/HistoryManagementPage';

// Lazy load less frequently accessed pages to improve initial load performance
const InstallPwaPage = lazy(() => import('../pages/pwa/InstallPwaPage'));
const AppInfoPage = lazy(() => import('../pages/pwa/AppInfoPage'));

const AppRoutes = () => {
  return (
    <Routes>
      {/* Home/Dashboard routes */}
      <Route path="/" element={<Dashboard />} />
      <Route path="/dashboard" element={<Navigate to="/" replace />} />
      <Route path="/home" element={<Navigate to="/" replace />} />
      
      {/* Student routes - Standardized to use /students/ prefix */}
      <Route path="/students" element={<Students />} />
      <Route path="/students/new" element={<StudentForm />} />
      <Route path="/students/:id" element={<StudentDetails />} />
      <Route path="/students/edit/:id" element={<StudentForm />} />
      <Route path="/students/new-list" element={<NewStudentsPage />} />
      
      {/* Document Management routes */}
      <Route path="/documents/manage" element={<DocumentManagementPage />} />
      <Route path="/documents" element={<Navigate to="/documents/manage" replace />} />
      
      {/* Payment routes */}
      <Route path="/students/:id/payment" element={<StudentPaymentPage />} />
      <Route path="/payments/student/:id" element={<Navigate to="/students/:id/payment" replace />} />
      <Route path="/teachers/:id/payment" element={<TeacherPaymentPage />} />
      
      {/* Exam routes */}
      <Route path="/students/:studentId/exams/new" element={<ExamResultForm />} />
      <Route path="/students/:studentId/exams/:examId" element={<ExamResultForm />} />
      
      {/* Programs routes */}
      <Route path="/programs/list" element={<ProgramsListPage />} />
      
      {/* Revenue routes */}
      <Route path="/revenue" element={<RevenuePage />} />
      
      {/* Legacy route for compatibility with existing links */}
      <Route path="/student/new" element={<Navigate to="/students/new" replace />} />
      <Route path="/student/:id" element={<Navigate to="/students/:id" replace />} />
      <Route path="/student/edit/:id" element={<Navigate to="/students/edit/:id" replace />} />
      
      {/* Teacher routes - Standardizing all routes to use /teachers/ prefix */}
      <Route path="/teachers" element={<TeachersList />} />
      <Route path="/teachers/overview" element={<TeachersOverviewPage />} />
      <Route path="/teachers/new" element={<TeacherForm />} />
      <Route path="/teachers/:id" element={<TeacherDetails />} />
      <Route path="/teachers/edit/:id" element={<TeacherForm />} />
      <Route path="/teachers/timetable" element={<TeacherTimetable />} />
      <Route path="/teachers/attendance" element={<TeacherAttendance />} />
      
      {/* Legacy teacher routes for backward compatibility */}
      <Route path="/teacher/new" element={<Navigate to="/teachers/new" replace />} />
      <Route path="/teacher/:id" element={<Navigate to="/teachers/:id" replace />} />
      <Route path="/teacher/edit/:id" element={<Navigate to="/teachers/edit/:id" replace />} />
      
      {/* Admin routes */}
      <Route path="/admins" element={<AdminsList />} />
      <Route path="/admins/new" element={<AdminForm />} />
      <Route path="/admins/:id" element={<AdminDetails />} />
      <Route path="/admins/edit/:id" element={<AdminForm />} />
      <Route path="/admins/history" element={<HistoryManagementPage />} />
      
      {/* Legacy admin routes for backward compatibility */}
      <Route path="/admin/new" element={<Navigate to="/admins/new" replace />} />
      <Route path="/admin/:id" element={<Navigate to="/admins/:id" replace />} />
      <Route path="/admin/edit/:id" element={<Navigate to="/admins/edit/:id" replace />} />
      <Route path="/admin" element={<Navigate to="/admins" replace />} />
      
      {/* Timetable routes */}
      <Route path="/timetable" element={<TeacherTimetable />} />
      <Route path="/timetable/alerts" element={<TimetableAlerts />} />
      <Route path="/timetable/new" element={<TimetableForm />} />
      <Route path="/timetable/edit/:classId" element={<TimetableForm />} />
      <Route path="/attendance" element={<TeacherAttendance />} />
      
      {/* Legacy timetable routes for backward compatibility */}
      <Route path="/schedule" element={<Navigate to="/timetable" replace />} />
      <Route path="/class-schedule" element={<Navigate to="/timetable" replace />} />
      <Route path="/teacher-schedule" element={<Navigate to="/timetable" replace />} />
      
      {/* Reports routes */}
      <Route path="/reports" element={<Reports />} />
      <Route path="/report" element={<Navigate to="/reports" replace />} /> {/* Legacy redirect */}
      
      {/* Certificate templates routes */}
      <Route path="/certificate-templates" element={<CertificateTemplatesPage />} />
      
      {/* Certificate template creation workflow routes */}
      <Route path="/certificate-templates/new/basic-info" element={<CreateTemplateBasicInfoPage />} />
      <Route path="/certificate-templates/new/upload" element={<CreateTemplateUploadPage />} />
      <Route path="/certificate-templates/new/mapping" element={<CreateTemplateMappingPage />} />
      <Route path="/certificate-templates/new/preview" element={<CreateTemplatePreviewPage />} />
      
      {/* Certificate template editing workflow routes */}
      <Route path="/certificate-templates/edit/:templateId/basic-info" element={<EditTemplateBasicInfoPage />} />
      <Route path="/certificate-templates/edit/:templateId/upload" element={<EditTemplateUploadPage />} />
      <Route path="/certificate-templates/edit/:templateId/mapping" element={<EditTemplateMappingPage />} />
      <Route path="/certificate-templates/edit/:templateId/preview" element={<EditTemplatePreviewPage />} />
      
      {/* Settings routes */}
      <Route path="/settings" element={<Settings />} />
      <Route path="/setting" element={<Navigate to="/settings" replace />} /> {/* Legacy redirect */}

      {/* Backup & Restore specific routes */}
      <Route 
        path="/settings/backup" 
        element={
          <Suspense fallback={<div className="flex justify-center items-center h-96">Loading...</div>}>
            <BackupManagementPage />
          </Suspense>
        } 
      />
      <Route path="/backup" element={<Navigate to="/settings/backup" replace />} /> {/* Legacy redirect */}
      <Route path="/backups" element={<Navigate to="/settings/backup" replace />} /> {/* Legacy redirect */}
      
      {/* Total Students Page */}
      <Route path="/total-students" element={<TotalStudentsPage />} />
      
      {/* PWA related routes */}
      <Route path="/offline" element={<OfflinePage />} />
      <Route 
        path="/install-pwa" 
        element={
          <Suspense fallback={<div className="flex justify-center items-center h-96">Loading...</div>}>
            <InstallPwaPage />
          </Suspense>
        } 
      />
      <Route 
        path="/app-info" 
        element={
          <Suspense fallback={<div className="flex justify-center items-center h-96">Loading...</div>}>
            <AppInfoPage />
          </Suspense>
        } 
      />
      
      {/* Other routes */}
      <Route path="/search" element={<Search />} />
      <Route path="/about-us" element={<AboutUs />} />
      <Route path="/documentation" element={<Documentation />} />
      <Route path="/terms-conditions" element={<TermsConditions />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/help-center" element={<HelpCenter />} />
      
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

export default AppRoutes;
